import React, { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { Plus, Trash2, Copy, Check } from "lucide-react";

const TABS = [
  { id: "leistungen", label: "Leistungskatalog" },
  { id: "tools", label: "Toolkatalog" },
  { id: "nutzer", label: "Nutzer" },
  { id: "invites", label: "Designer-Einladungen" },
  { id: "vorlagen", label: "E-Mail-Vorlagen" },
];

const BEREICHE = ["Brand", "Digital", "Print"];

export default function Verwaltung() {
  const [tab, setTab] = useState("leistungen");
  return (
    <div>
      <div className="mb-8">
        <div className="mono-label">Admin</div>
        <h1 className="font-archivo font-bold text-4xl mt-1">Verwaltung</h1>
      </div>

      <div className="flex gap-1 mb-6 border-b border-line">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`verw-tab-${t.id}`}
            className={`px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-ink text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === "leistungen" && <LeistungenTab />}
      {tab === "tools" && <ToolsTab />}
      {tab === "nutzer" && <NutzerTab />}
      {tab === "invites" && <InvitesTab />}
      {tab === "vorlagen" && <VorlagenTab />}
    </div>
  );
}

function VorlagenTab() {
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/email-templates/kunden_freigabe");
        setTpl(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Vorlage konnte nicht geladen werden.");
      }
    })();
  }, []);

  const save = async () => {
    if (!tpl) return;
    setBusy(true); setMsg(""); setErr("");
    try {
      await api.put("/admin/email-templates/kunden_freigabe", {
        betreff: tpl.betreff, body: tpl.body,
      });
      setMsg("Vorlage gespeichert.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Fehler beim Speichern.");
    } finally { setBusy(false); }
  };

  if (!tpl) return <div className="mono-label">Lade…</div>;

  return (
    <div className="paper-card p-6 max-w-3xl">
      <h3 className="font-archivo font-bold text-lg mb-1">Kundenfreigabe-Mail</h3>
      <div className="mono-label mb-4">
        Wird verschickt, wenn ein PM einen Entwurf an einen Kundenkontakt schickt.
        Platzhalter: <code className="text-xs">{"{kunde}"}</code>, <code className="text-xs">{"{kunde_kontakt}"}</code>,
        <code className="text-xs"> {"{projekt}"}</code>, <code className="text-xs"> {"{link}"}</code>,
        <code className="text-xs"> {"{pm_name}"}</code>, <code className="text-xs"> {"{pm_notiz_block}"}</code>
      </div>

      <div className="mb-4">
        <label className="mono-label block mb-1">Betreff</label>
        <input
          value={tpl.betreff}
          onChange={(e) => setTpl((s) => ({ ...s, betreff: e.target.value }))}
          data-testid="vorlage-betreff"
          className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm"
        />
      </div>
      <div className="mb-4">
        <label className="mono-label block mb-1">Text (mit Zeilenumbrüchen)</label>
        <textarea
          value={tpl.body}
          onChange={(e) => setTpl((s) => ({ ...s, body: e.target.value }))}
          data-testid="vorlage-body"
          rows={14}
          className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm font-mono"
        />
      </div>

      {msg && <div className="text-sm mb-3 px-3 py-2 rounded-sm" style={{ color: "#157A4A", backgroundColor: "#DDEEE3" }}>{msg}</div>}
      {err && <div className="text-sm mb-3 px-3 py-2 rounded-sm" style={{ color: "#BE2E6C", backgroundColor: "#F7DDE7" }}>{err}</div>}

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={save}
          disabled={busy}
          data-testid="vorlage-save"
          className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white rounded-sm text-sm hover:bg-black disabled:opacity-50"
        >
          {busy ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

function NutzerTab() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", email: "", rolle: "pm", telefon: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/admin/users", f);
      setShowForm(false);
      setF({ name: "", email: "", rolle: "pm", telefon: "", password: "" });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Fehler beim Anlegen");
    } finally { setBusy(false); }
  };

  const roleColor = { admin: "#1B1E23", pm: "#0E62C4", designer: "#BE2E6C" };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="mono-label">{users.length} Nutzer</div>
        <button onClick={() => setShowForm(true)} data-testid="user-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white rounded-sm text-sm hover:bg-black">
          <Plus size={14} /> Neuer Nutzer
        </button>
      </div>

      <div className="paper-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-paper/60">
              <th className="text-left mono-label py-3 px-5 font-medium">Name</th>
              <th className="text-left mono-label py-3 px-5 font-medium">E-Mail</th>
              <th className="text-left mono-label py-3 px-5 font-medium">Telefon</th>
              <th className="text-left mono-label py-3 px-5 font-medium">Rolle</th>
              <th className="text-left mono-label py-3 px-5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-line/60 last:border-0" data-testid={`user-row-${u.id}`}>
                <td className="py-3 px-5 font-medium">{u.name}</td>
                <td className="py-3 px-5 font-mono text-xs">{u.email}</td>
                <td className="py-3 px-5">{u.telefon || "—"}</td>
                <td className="py-3 px-5">
                  <span className="mono-label" style={{ color: roleColor[u.rolle] || "#1B1E23", opacity: 1 }}>{u.rolle}</span>
                </td>
                <td className="py-3 px-5">
                  <span className="status-pill" style={{
                    color: u.aktiv ? "#157A4A" : "#8A5A00",
                    backgroundColor: u.aktiv ? "#DDEEE3" : "#FBEED8"
                  }}>{u.aktiv ? "aktiv" : "inaktiv"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-line rounded-md p-8 w-full max-w-md space-y-4" data-testid="user-form">
            <h3 className="font-archivo font-bold text-xl">Neuer Nutzer</h3>
            <div>
              <label className="mono-label block mb-1">Rolle</label>
              <select value={f.rolle} onChange={(e) => setF({ ...f, rolle: e.target.value })} data-testid="user-rolle" className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                <option value="pm">Projektmanager (PM)</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="mono-label block mb-1">Name *</label>
              <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
            </div>
            <div>
              <label className="mono-label block mb-1">E-Mail *</label>
              <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
            </div>
            <div>
              <label className="mono-label block mb-1">Telefon</label>
              <input value={f.telefon} onChange={(e) => setF({ ...f, telefon: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
            </div>
            <div>
              <label className="mono-label block mb-1">Passwort (min. 6) *</label>
              <input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
            </div>
            {err && <div className="text-sm text-brunnerMagenta">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-line rounded-sm">Abbrechen</button>
              <button type="submit" disabled={busy} data-testid="user-submit" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
                {busy ? "Anlegen…" : "Anlegen"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function LeistungenTab() {
  const [items, setItems] = useState([]);
  const [bereich, setBereich] = useState("Brand");
  const [bezeichnung, setBezeichnung] = useState("");

  const load = async () => setItems((await api.get("/admin/katalog")).data.leistungen);
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!bezeichnung.trim()) return;
    try {
      await api.post("/admin/katalog/leistungen", { bereich, bezeichnung });
      setBezeichnung(""); await load();
    } catch {}
  };
  const del = async (id) => {
    if (!window.confirm("Löschen?")) return;
    await api.delete(`/admin/katalog/leistungen/${id}`); await load();
  };

  const grouped = BEREICHE.reduce((acc, b) => { acc[b] = items.filter((i) => i.bereich === b); return acc; }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="paper-card p-6 h-fit">
        <h3 className="font-archivo font-bold text-lg mb-4">Leistung hinzufügen</h3>
        <form onSubmit={add} className="space-y-3">
          <div>
            <label className="mono-label block mb-1">Bereich</label>
            <select value={bereich} onChange={(e) => setBereich(e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white">
              {BEREICHE.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="mono-label block mb-1">Bezeichnung</label>
            <input value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} data-testid="leist-bez"
              className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
          </div>
          <button type="submit" data-testid="leist-add" className="w-full inline-flex items-center justify-center gap-2 py-2 bg-ink text-white rounded-sm hover:bg-black">
            <Plus size={14} /> Hinzufügen
          </button>
        </form>
      </div>

      <div className="lg:col-span-2 space-y-4">
        {BEREICHE.map((b) => (
          <div key={b} className="paper-card p-5">
            <div className="flex items-baseline justify-between mb-3">
              <h4 className="font-archivo font-bold text-base">{b}</h4>
              <span className="mono-label">{grouped[b].length}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {grouped[b].map((l) => (
                <span key={l.id} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border border-line rounded-sm bg-paper" data-testid={`leist-${l.id}`}>
                  {l.bezeichnung}
                  <button onClick={() => del(l.id)} className="text-ink/40 hover:text-brunnerMagenta"><Trash2 size={11} /></button>
                </span>
              ))}
              {grouped[b].length === 0 && <span className="text-xs text-ink/50">Keine Einträge</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ToolsTab() {
  const [items, setItems] = useState([]);
  const [bez, setBez] = useState("");
  const load = async () => setItems((await api.get("/admin/katalog")).data.tools);
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault(); if (!bez.trim()) return;
    try { await api.post("/admin/katalog/tools", { bezeichnung: bez }); setBez(""); await load(); } catch {}
  };
  const del = async (id) => { if (window.confirm("Löschen?")) { await api.delete(`/admin/katalog/tools/${id}`); await load(); } };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="paper-card p-6 h-fit">
        <h3 className="font-archivo font-bold text-lg mb-4">Tool hinzufügen</h3>
        <form onSubmit={add} className="space-y-3">
          <input value={bez} onChange={(e) => setBez(e.target.value)} placeholder="z. B. Sketch" data-testid="tool-bez" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
          <button type="submit" data-testid="tool-add" className="w-full inline-flex items-center justify-center gap-2 py-2 bg-ink text-white rounded-sm hover:bg-black">
            <Plus size={14} /> Hinzufügen
          </button>
        </form>
      </div>
      <div className="lg:col-span-2 paper-card p-6">
        <div className="flex flex-wrap gap-1.5">
          {items.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1.5 text-sm px-3 py-1 border border-line rounded-sm bg-paper" data-testid={`tool-${t.id}`}>
              {t.bezeichnung}
              <button onClick={() => del(t.id)} className="text-ink/40 hover:text-brunnerMagenta"><Trash2 size={11} /></button>
            </span>
          ))}
          {items.length === 0 && <div className="text-sm text-ink/50">Keine Tools.</div>}
        </div>
      </div>
    </div>
  );
}

function InvitesTab() {
  const [items, setItems] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const load = async () => setItems((await api.get("/admin/designer-invites")).data);
  useEffect(() => { load(); }, []);

  const copyLink = (t, id) => {
    navigator.clipboard.writeText(`${window.location.origin}/invite/${t}`);
    setCopiedId(id); setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="paper-card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-paper/60">
            <th className="text-left mono-label py-3 px-5 font-medium">Name</th>
            <th className="text-left mono-label py-3 px-5 font-medium">E-Mail</th>
            <th className="text-left mono-label py-3 px-5 font-medium">Erstellt</th>
            <th className="text-left mono-label py-3 px-5 font-medium">Status</th>
            <th className="text-left mono-label py-3 px-5 font-medium">Link</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="border-b border-line/60 last:border-0">
              <td className="py-3 px-5">{i.name}</td>
              <td className="py-3 px-5 font-mono text-xs">{i.email}</td>
              <td className="py-3 px-5 font-mono text-xs">{fmtDate(i.erstellt_am)}</td>
              <td className="py-3 px-5"><span className="status-pill" style={{
                color: i.verwendet ? "#157A4A" : "#8A5A00",
                backgroundColor: i.verwendet ? "#DDEEE3" : "#FBEED8"
              }}>{i.verwendet ? "verwendet" : "offen"}</span></td>
              <td className="py-3 px-5">
                {!i.verwendet && (
                  <button onClick={() => copyLink(i.token, i.id)} className="mono-label inline-flex items-center gap-1 hover:opacity-100">
                    {copiedId === i.id ? <><Check size={11} /> Kopiert</> : <><Copy size={11} /> Link kopieren</>}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-ink/50">Keine Einladungen</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
